import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CryptoService, LocalStorageService } from 'src/app/core/core.module';
import { NgOtpInputComponent } from 'ng-otp-input';
import { FundsPortalService } from 'src/app/core/services/funds-portal.service';
import { AtramModalComponent } from 'src/app/shared/components/atram-modal/atram-modal.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-totp-enroll',
  templateUrl: './totp-enroll.component.html',
  styleUrls: ['./totp-enroll.component.less']
})
export class TotpEnrollComponent implements OnInit {

  @ViewChild('atramModal')
  public atramModal: AtramModalComponent = new AtramModalComponent;

  @ViewChild('ngOtpInput')
  public ngOtpInput!: NgOtpInputComponent;

  public searchFormGroup!: FormGroup;
  public qrCodeUri: string = '';
  public manualSecret: string = '';
  public isError: boolean = false;
  public isInvalidCode: boolean = false;
  public isNotValidInputs: boolean = false;
  public isLoading: boolean = true;
  public backupCodes: string[] = [];
  public copyFeedback: string = '';

  constructor(
    public fundsPortalService: FundsPortalService,
    public router: Router,
    public localStorageService: LocalStorageService,
    public cryptoService: CryptoService
  ) {}

  ngOnInit(): void {
    this.initializeFormGroup();
    this.initiateMfaSetup();
  }

  initializeFormGroup(): void {
    this.searchFormGroup = new FormGroup({
      otp: new FormControl('', [Validators.required])
    });
  }

  initiateMfaSetup(): void {
    // login enrollment uses bearer token (already in localStorage);
    // sign-up enrollment uses invite_code
    const isLoginEnroll = this.localStorageService.get('enroll_context') === 'login';
    const temporaryPassword = this.localStorageService.get('temporary_password');

    if (isLoginEnroll && !temporaryPassword) {
      this.router.navigateByUrl('/sign-up/email-otp-verify', { skipLocationChange: false });
      return;
    }

    const body = isLoginEnroll
      ? { temporary_password: temporaryPassword }
      : { invite_code: this.localStorageService.get('email_code') };

    this.fundsPortalService.postInitiateMfaSetup(body).subscribe({
      next: (data: any) => {
        this.qrCodeUri = data['response_data']['otpauth_uri'];
        this.manualSecret = data['response_data']['manual_entry_key'];
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;

        const errorMessage: string =
          error?.response_data?.message ||
          (typeof error?.response_data === 'string' ? error.response_data : null) ||
          error?.response_description ||
          'Could not initiate MFA setup.';

        const isChallengeError = /invalid|expired|challenge|temporary|unauthorized|auth|token|401/i.test(errorMessage);

        const isLocked = error?.response_data?.is_locked === true;
        if (isLocked) {
          const lockoutSeconds: number = error?.response_data?.lockout_duration_seconds ?? 300;
          const lockoutMinutes = Math.ceil(lockoutSeconds / 60);
          this.atramModal.show();
          this.atramModal.size = 'sm';
          this.atramModal.status = 'error';
          this.atramModal.headerText = 'Account Temporarily Locked';
          this.atramModal.bodyText = `Your account has been temporarily locked due to too many incorrect attempts. Please wait ${lockoutMinutes} minute(s) before trying again.`;
          this.atramModal.okButtonText = 'OK';
          this.atramModal.showCancelButton = false;
          this.atramModal.showCloseButton = false;
          this.atramModal.confirmationSubject.subscribe(() => {
            this.atramModal.hide();
            this.localStorageService.remove('temporary_password');
            this.localStorageService.set('enroll_context', 'login', 900);
            this.localStorageService.set('setup_lockout_seconds', String(lockoutSeconds), lockoutSeconds + 60);
            this.router.navigateByUrl('/sign-up/email-otp-verify', { skipLocationChange: false });
          });
          return;
        }

        if (isChallengeError) {
          this.localStorageService.remove('temporary_password');
          this.localStorageService.set('enroll_context', 'login', 900);
          this.localStorageService.set('setup_challenge_error', errorMessage, 300);

          const failedAttemptCount: number = error?.response_data?.failed_attempt_count ?? 0;
          if (failedAttemptCount >= 4) {
            this.atramModal.show();
            this.atramModal.size = 'sm';
            this.atramModal.status = 'warning';
            this.atramModal.headerText = 'Warning: One Attempt Remaining';
            this.atramModal.bodyText = 'You have entered an incorrect temporary password (four) 4 times. One more incorrect attempt will temporarily lock your account.';
            this.atramModal.okButtonText = 'OK';
            this.atramModal.showCancelButton = false;
            this.atramModal.showCloseButton = false;
            this.atramModal.confirmationSubject.subscribe(() => {
              this.atramModal.hide();
              this.router.navigateByUrl('/sign-up/email-otp-verify', { skipLocationChange: false });
            });
            return;
          }

          this.router.navigateByUrl('/sign-up/email-otp-verify', { skipLocationChange: false });
          return;
        }

        this.atramModal.show();
        this.atramModal.size = 'sm';
        this.atramModal.status = 'error';
        this.atramModal.headerText = 'Setup Failed';
        this.atramModal.bodyText = errorMessage;
        this.atramModal.okButtonText = 'Return';
        this.atramModal.showCancelButton = false;
        this.atramModal.showCloseButton = false;
        this.atramModal.confirmationSubject.subscribe((confirmation: boolean) => {
          if (confirmation) {
            this.router.navigateByUrl('', { skipLocationChange: false });
          }
          this.atramModal.hide();
        });
      }
    });
  }

  onOtpChange(e: any): void {
    this.searchFormGroup.get('otp')?.setValue(e);
    if (this.searchFormGroup.get('otp')?.value.length === 6) {
      this.isError = false;
      this.isInvalidCode = false;
    }
  }

  onOtpKeydown(event: KeyboardEvent): void {
    const isAllowed = /^[0-9]$/.test(event.key) ||
      ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key);
    if (!isAllowed) {
      // Re-sync ng-otp-input internal focus tracker to prevent cursor corruption
      setTimeout(() => {
        if (this.ngOtpInput) {
          const current = this.searchFormGroup.get('otp')?.value || '';
          this.ngOtpInput.setValue(current);
        }
      }, 0);
    }
  }

  proceed(): void {
    if (this.searchFormGroup.get('otp')?.value.length < 6) {
      this.isError = true;
      this.isNotValidInputs = true;
      return;
    }
    this.isNotValidInputs = false;
    this.submit();
  }

  submit(): void {
    const code = this.searchFormGroup.get('otp')?.value;
    const isLoginEnroll = this.localStorageService.get('enroll_context') === 'login';
    const temporaryPassword = this.localStorageService.get('temporary_password');
    const body = isLoginEnroll
      ? { code: code, temporary_password: temporaryPassword }
      : { invite_code: this.localStorageService.get('email_code'), code: code };

    this.fundsPortalService.postCompleteMfaSetup(body).subscribe({
      next: (data: any) => {
        if (data['response_result'] === '0001') {
          const nextRoute = '/file-upload/mutual-funds';
          const finalToken = data['response_data']['access_token'];
          if (finalToken) {
            this.localStorageService.set('bearer', finalToken, data['response_data']['expires_in']);
            this.localStorageService.set('authorized', '0001', data['response_data']['expires_in']);
          }
          this.localStorageService.remove('temporary_password');
          this.localStorageService.remove('enroll_context');

          this.atramModal.show();
          this.atramModal.size = 'sm';
          this.atramModal.status = 'success';
          this.atramModal.headerText = 'MFA Enrollment Successful';
          this.atramModal.bodyText = 'Your authenticator has been successfully linked to your account.';
          this.atramModal.okButtonText = 'Continue';
          this.atramModal.showCancelButton = false;
          this.atramModal.showCloseButton = false;
          this.atramModal.confirmationSubject.subscribe((confirmation: boolean) => {
            if (confirmation) {
              this.router.navigateByUrl(nextRoute, { skipLocationChange: false });
            }
            this.atramModal.hide();
          });
        } else {
          this.isInvalidCode = true;
        }
      },
      error: error => {
        this.isInvalidCode = true;
      }
    });
  }

  copyBackupCodes(): void {
    const text = this.backupCodes.join('\n');
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        this.copyFeedback = 'Backup codes copied to clipboard.';
      }).catch(() => {
        this.copyFeedback = 'Unable to copy. Please copy them manually.';
      });
    } else {
      this.copyFeedback = 'Unable to copy. Please copy them manually.';
    }
  }

  continueToSignIn(): void {
    const isLoginEnroll = this.localStorageService.get('enroll_context') === 'login';
    this.localStorageService.remove('temporary_password');
    this.localStorageService.remove('enroll_context');
    if (isLoginEnroll) {
      this.router.navigateByUrl('/file-upload/mutual-funds', { skipLocationChange: false });
    } else {
      this.router.navigateByUrl('', { skipLocationChange: false });
    }
  }
}